/**
 * Settings page — Copilot autonomy controls.
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Lock, Loader2, FlaskConical, Copy } from 'lucide-react';
import { Switch } from '@/client/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/client/components/ui/select';
import { useSkillBranches } from '@/client/lib/useSkillBranches';
import { useAuth } from '@/client/app/providers/auth-context';

/* ─── View Tools (Gorgias-only dev tool) ─────────────────────────── */

const VIEW_TOOLS_KEY = 'gaia-view-tools';

function readViewTools(): boolean {
  try {
    const stored = localStorage.getItem(VIEW_TOOLS_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

function writeViewTools(value: boolean) {
  try {
    localStorage.setItem(VIEW_TOOLS_KEY, value ? 'true' : 'false');
  } catch {
    /* private browsing */
  }
}

/* ─── Skill Branch (Gorgias-only dev tool) ───────────────────────── */

const SKILL_BRANCH_KEY = 'gaia-embed-skill-branch';

function readSkillBranch(): string {
  try {
    return localStorage.getItem(SKILL_BRANCH_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeSkillBranch(value: string) {
  try {
    if (value) {
      localStorage.setItem(SKILL_BRANCH_KEY, value);
    } else {
      localStorage.removeItem(SKILL_BRANCH_KEY);
    }
  } catch {
    /* private browsing */
  }
}

/* ─── Reusable Setting Rows ──────────────────────────────────────── */

function SettingToggle({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-muted/50">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium leading-tight">{title}</div>
        <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} className="shrink-0 scale-75" />
    </label>
  );
}

function SettingSelect({
  title,
  description,
  value,
  onChange,
  options,
}: {
  title: string;
  description: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-medium leading-tight">{title}</div>
        <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{description}</div>
      </div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-7 w-[110px] shrink-0 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ─── Section ────────────────────────────────────────────────────── */

function SettingSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
        <div className="h-px flex-1 bg-border" />
      </div>
      <div className="flex flex-col gap-1.5">{children}</div>
    </div>
  );
}

/* ─── Main Settings View ─────────────────────────────────────────── */

export function SettingsView() {
  const { userEmail, getAppsToken } = useAuth();
  const isGorgiasUser = userEmail?.endsWith('@gorgias.com') ?? false;
  const copyAppsToken = useCallback(() => {
    const token = getAppsToken?.();
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => {
      toast.success('Apps token copied to clipboard');
    });
  }, [getAppsToken]);

  // Dev tools (Gorgias-only)
  const [viewTools, setViewTools] = useState(readViewTools);
  const [skillBranch, setSkillBranch] = useState(readSkillBranch);
  const { branches, loading: branchesLoading } = useSkillBranches(isGorgiasUser ? '/api/skill-branches' : null);

  // Mock state — auto-publish
  const [autoPublishGuidances, setAutoPublishGuidances] = useState(false);
  const [autoPublishActions, setAutoPublishActions] = useState(false);
  const [autoEnableActions, setAutoEnableActions] = useState(false);

  // Mock state — audit & suggestions
  const [auditSchedule, setAuditSchedule] = useState('weekly');
  const [suggestionsPerRound, setSuggestionsPerRound] = useState('5');
  const [autoCreateDrafts, setAutoCreateDrafts] = useState(true);
  const [includeFlowConversion, setIncludeFlowConversion] = useState(true);

  // Mock state — reporting
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [anomalyAlerts, setAnomalyAlerts] = useState(false);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex flex-col gap-5 p-4">

        {/* Mockup disclaimer */}
        <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
          <FlaskConical className="mt-0.5 size-4 shrink-0" />
          <div>
            <p className="text-xs font-medium">Preview — settings are not saved yet</p>
            <p className="mt-0.5 text-[11px] leading-snug opacity-80">These controls are a preview of upcoming Gaia autonomy features. Changes are not persisted.</p>
          </div>
        </div>

        <SettingSection title="Publishing">
          <SettingToggle
            title="Auto-publish guidances"
            description="Publish guidance articles without manual review"
            checked={autoPublishGuidances}
            onChange={setAutoPublishGuidances}
          />
          <SettingToggle
            title="Auto-publish actions"
            description="Create and deploy support actions without review"
            checked={autoPublishActions}
            onChange={setAutoPublishActions}
          />
          <SettingToggle
            title="Auto-enable actions"
            description="Enable new actions immediately instead of creating them disabled"
            checked={autoEnableActions}
            onChange={setAutoEnableActions}
          />
        </SettingSection>

        <SettingSection title="Audit & Suggestions">
          <SettingSelect
            title="Audit schedule"
            description="How often Gaia audits your setup and suggests improvements"
            value={auditSchedule}
            onChange={setAuditSchedule}
            options={[
              { value: 'daily', label: 'Daily' },
              { value: 'weekly', label: 'Weekly' },
              { value: 'biweekly', label: 'Every 2 weeks' },
              { value: 'monthly', label: 'Monthly' },
              { value: 'manual', label: 'Manual only' },
            ]}
          />
          <SettingSelect
            title="Suggestions per round"
            description="Max improvement suggestions per audit cycle"
            value={suggestionsPerRound}
            onChange={setSuggestionsPerRound}
            options={[
              { value: '3', label: '3' },
              { value: '5', label: '5' },
              { value: '10', label: '10' },
              { value: '15', label: '15' },
              { value: '20', label: '20' },
            ]}
          />
          <SettingToggle
            title="Auto-create drafts"
            description="Create draft guidances automatically when opportunities are found"
            checked={autoCreateDrafts}
            onChange={setAutoCreateDrafts}
          />
          <SettingToggle
            title="Include flow conversion"
            description="Suggest converting published flows into guidance articles"
            checked={includeFlowConversion}
            onChange={setIncludeFlowConversion}
          />
        </SettingSection>

        <SettingSection title="Reporting">
          <SettingToggle
            title="Weekly performance digest"
            description="Summary of AI Agent performance, ticket trends, and Gaia activity"
            checked={weeklyDigest}
            onChange={setWeeklyDigest}
          />
          <SettingToggle
            title="Anomaly alerts"
            description="Notify when unusual patterns in volume, resolution, or CSAT are detected"
            checked={anomalyAlerts}
            onChange={setAnomalyAlerts}
          />
        </SettingSection>

        {isGorgiasUser && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Developer</span>
              <div className="h-px flex-1 bg-border" />
              <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                <Lock className="size-2.5" />
                Gorgias only
              </span>
            </div>
            <SettingToggle
              title="View tools"
              description="Show full tool call cards in messages (debug mode)"
              checked={viewTools}
              onChange={(v) => { setViewTools(v); writeViewTools(v); }}
            />
            <div className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium leading-tight">Apps token</div>
                <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  Copy the current Gorgias Apps Token to clipboard
                </div>
              </div>
              <button
                onClick={copyAppsToken}
                className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors hover:bg-muted"
              >
                <Copy className="size-3" />
                Copy
              </button>
            </div>
            <div className="rounded-lg px-3 py-2.5">
              <div className="text-[13px] font-medium leading-tight">Skill branch</div>
              <div className="mt-0.5 mb-2 text-[11px] leading-snug text-muted-foreground">
                Override skills with a PR branch for testing
              </div>
              {branchesLoading ? (
                <div className="flex h-7 items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" />
                  Loading...
                </div>
              ) : (
                <Select value={skillBranch || '__none__'} onValueChange={(v) => { const val = v === '__none__' ? '' : v; setSkillBranch(val); writeSkillBranch(val); }}>
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None (default skills)</SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch} value={branch}>
                        {branch}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
